import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Drawer, IconButton, TextField,
  InputAdornment, Button, CircularProgress, Divider, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { updateProfileDataApi, updatePasswordApi } from '../../api';
import { updateUser } from '../../redux/userSlice';
import { useConfirm } from '../confirmDialog/useConfirm';
import ImageUploader from '../imguploader/ImageUploader';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#fff',
    fontFamily: 'Heebo',
    '& fieldset': { borderColor: '#E7E7E7' },
    '&:hover fieldset': { borderColor: '#D7D7D7' },
    '&.Mui-focused fieldset': { borderColor: '#065F46' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'Heebo' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#065F46' },
  '& .MuiFormHelperText-root': { fontFamily: 'Heebo', marginLeft: '4px', fontSize: '11px' },
};

const TAB_LABELS = ['Edit Profile', 'Change Password'];

const UserProfile = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const token = useSelector((s) => s?.user?.user?.token);
  const users = useSelector((s) => s?.user?.user);
  const confirm = useConfirm();

  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Edit Profile state
  const [profile, setProfile] = useState({
    firstName: '', lastName: '', email: '', phoneNo: '', picture: '',
  });

  // Change Password state
  const [pass, setPass] = useState({ currentPassword: '', newPassword: '', cPassword: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });

  useEffect(() => {
    if (open) {
      setProfile({
        firstName: users?.firstName || '',
        lastName: users?.lastName || '',
        email: users?.email || '',
        picture: users?.picture || '',
        phoneNo: users?.phone || '',
      });
      setPass({ currentPassword: '', newPassword: '', cPassword: '' });
      setShowPw({ current: false, new: false, confirm: false });
    }
  }, [open, users]);

  const handleClose = () => {
    setActiveTab(0);
    onClose();
  };

  const handleUpdateProfile = async () => {
    if (!profile.firstName.trim() || !profile.lastName.trim()) {
      toast.warning('First and last name are required');
      return;
    }
    if (profile.phoneNo && !/^\d{10}$/.test(profile.phoneNo)) {
      toast.error('Mobile number must be 10 digits');
      return;
    }
    const ok = await confirm({
      title: 'Update Profile',
      description: 'Are you sure you want to update your details?',
      confirmText: 'Update',
    });
    if (!ok) return;
    setSaving(true);
    try {
      const response = await updateProfileDataApi({ token, user: profile });
      dispatch(updateUser(response?.data));
      toast.success(response?.message);
      handleClose();
    } catch (error) {
      toast.error(error?.error || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const { currentPassword, newPassword, cPassword } = pass;
    if (!currentPassword || !newPassword || !cPassword) {
      toast.warning('Please fill in all fields');
      return;
    }
    if (newPassword !== cPassword) {
      toast.error('New and confirm passwords do not match');
      return;
    }
    if ([currentPassword, newPassword, cPassword].some((p) => p.length < 8)) {
      toast.error('All passwords must be at least 8 characters');
      return;
    }
    const ok = await confirm({
      title: 'Update Password',
      description: 'Are you sure you want to update your password?',
      confirmText: 'Update',
    });
    if (!ok) return;
    setSaving(true);
    try {
      const response = await updatePasswordApi({ allPass: pass, token });
      toast.success(response?.message);
      setPass({ currentPassword: '', newPassword: '', cPassword: '' });
      handleClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (activeTab === 0) handleUpdateProfile();
    else handleUpdatePassword();
  };

  const togglePw = (field) => setShowPw((p) => ({ ...p, [field]: !p[field] }));

  const saveLabel = activeTab === 0 ? 'Save Changes' : 'Update Password';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          background: '#fff',
          borderTopLeftRadius: { sm: '12px' },
          borderBottomLeftRadius: { sm: '12px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* HEADER */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #F1F1F1', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#362B24', fontFamily: 'Heebo', lineHeight: 1.3 }}>
            Manage Profile
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#8B8B8B', mt: 0.4, fontFamily: 'Heebo' }}>
            {users?.firstName ? `${users.firstName} ${users.lastName || ''}`.trim() : 'Your account settings'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ border: '1px solid #EBEBEB', width: 36, height: 36, borderRadius: '6px', flexShrink: 0, ml: 1 }}>
          <CloseIcon sx={{ fontSize: 18, color: '#362B24' }} />
        </IconButton>
      </Box>

      {/* TAB SWITCHER */}
      <Box sx={{ px: 3, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            background: '#F4F4F4',
            borderRadius: '8px',
            p: '4px',
            gap: '4px',
          }}
        >
          {TAB_LABELS.map((label, i) => (
            <Button
              key={i}
              disableRipple
              onClick={() => setActiveTab(i)}
              sx={{
                flex: 1,
                height: '36px',
                borderRadius: '5px',
                fontFamily: 'Heebo',
                fontSize: '13px',
                fontWeight: activeTab === i ? 600 : 400,
                textTransform: 'none',
                background: activeTab === i ? '#fff' : 'transparent',
                color: activeTab === i ? '#362B24' : '#8B8B8B',
                boxShadow: activeTab === i ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                '&:hover': { background: activeTab === i ? '#fff' : 'rgba(0,0,0,0.04)' },
                transition: 'all 0.15s ease',
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* BODY */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {activeTab === 0 ? (
          <>
            {/* Avatar */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <ImageUploader
                data="Change Photo"
                picture={profile.picture}
                setPicture={(pic) => setProfile((p) => ({ ...p, picture: pic }))}
                avatarSize={96}
              />
            </Box>

            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth size="small" label="First Name" value={profile.firstName}
                  onChange={(e) => {
                    if (e.target.value.trim() !== '' || e.target.value === '')
                      setProfile((p) => ({ ...p, firstName: e.target.value }));
                  }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth size="small" label="Last Name" value={profile.lastName}
                  onChange={(e) => {
                    if (e.target.value.trim() !== '' || e.target.value === '')
                      setProfile((p) => ({ ...p, lastName: e.target.value }));
                  }}
                  sx={inputSx}
                />
              </Box>
              <TextField
                fullWidth size="small" label="Email Address" value={profile.email}
                disabled
                helperText="Email cannot be changed"
                sx={inputSx}
              />
              <TextField
                fullWidth size="small" label="Mobile Number" value={profile.phoneNo}
                onChange={(e) => {
                  if (/^\d{0,10}$/.test(e.target.value))
                    setProfile((p) => ({ ...p, phoneNo: e.target.value }));
                }}
                helperText="10-digit mobile number"
                sx={inputSx}
              />
            </Stack>
          </>
        ) : (
          <>
            <Box sx={{ mb: 3, p: 2, background: '#F8FBF9', borderRadius: '8px', border: '1px solid #E8F5E9' }}>
              <Typography sx={{ fontSize: '13px', color: '#065F46', fontFamily: 'Heebo', fontWeight: 500 }}>
                Choose a strong password with at least 8 characters.
              </Typography>
            </Box>

            <Stack spacing={2}>
              <PasswordField
                label="Current Password"
                value={pass.currentPassword}
                onChange={(v) => setPass((p) => ({ ...p, currentPassword: v }))}
                show={showPw.current}
                onToggle={() => togglePw('current')}
                inputSx={inputSx}
              />
              <Divider sx={{ borderColor: '#F4F4F4' }} />
              <PasswordField
                label="New Password"
                value={pass.newPassword}
                onChange={(v) => setPass((p) => ({ ...p, newPassword: v }))}
                show={showPw.new}
                onToggle={() => togglePw('new')}
                helperText="Minimum 8 characters"
                inputSx={inputSx}
              />
              <PasswordField
                label="Confirm New Password"
                value={pass.cPassword}
                onChange={(v) => setPass((p) => ({ ...p, cPassword: v }))}
                show={showPw.confirm}
                onToggle={() => togglePw('confirm')}
                error={pass.cPassword && pass.newPassword !== pass.cPassword}
                helperText={pass.cPassword && pass.newPassword !== pass.cPassword ? 'Passwords do not match' : ''}
                inputSx={inputSx}
              />
            </Stack>
          </>
        )}
      </Box>

      {/* FOOTER */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #F1F1F1', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0, background: '#fff' }}>
        <Button
          onClick={handleClose}
          sx={{ borderRadius: '8px', height: '41px', px: 3, border: '1px solid #EBEBEB', color: '#362B24', fontFamily: 'Heebo', textTransform: 'none', fontSize: '14px' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          sx={{
            borderRadius: '8px', height: '41px', px: 4, minWidth: 140,
            background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)',
            color: '#fff', fontFamily: 'Heebo', fontWeight: 600, textTransform: 'none', fontSize: '14px',
            boxShadow: '1px 2px 30px 0px rgba(0,0,0,0.06)',
            '&:hover': { opacity: 0.9, background: 'linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)' },
            '&:disabled': { opacity: 0.6, color: '#fff' },
          }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : saveLabel}
        </Button>
      </Box>
    </Drawer>
  );
};

function PasswordField({ label, value, onChange, show, onToggle, helperText, error, inputSx }) {
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={(e) => {
        if (e.target.value.trim() !== '' || e.target.value === '') onChange(e.target.value);
      }}
      error={!!error}
      helperText={helperText || ''}
      sx={inputSx}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={onToggle}>
              {show
                ? <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#8B8B8B' }} />
                : <VisibilityOffOutlinedIcon sx={{ fontSize: 18, color: '#8B8B8B' }} />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

export default UserProfile;
